import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import CollectionPopup from '@/components/organisms/CollectionPopup';
import DeliveryPopup from '@/components/organisms/DeliveryPopup';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import useGetDelivery from '@/hooks/api/useGetDelivery';
import useGetDeviceDeliCollInfo from '@/hooks/api/useGetDeviceDeliCollInfo';
import { deliveryState } from '@/states/deliveryState';
import {
  CollectionInfoState,
  DeliveryInfoState,
  DeviceDeliCollInfoState,
} from '@/states/deviceDeliCollInfoState';
import { CollectionInfo, DeliveryInfo } from '@/types/api/mypage/deviceDeliCollInfo';
import { NextPage } from 'next';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

/**
 * 配送情報トップページ
 * 端末の配送状況や回収状況を一覧で表示するページ。
 * 配送先変更や回収先変更への導線を提供する。
 */
const DeliveryIndex: NextPage = () => {
  // API呼び出しhooks
  const { execute: getDelivery } = useGetDelivery();
  const { execute: getDeviceDeliCollInfo } = useGetDeviceDeliCollInfo();
  // hooks
  const deliveryInfo = useRecoilValue(deliveryState);
  const deviceDeliCollInfo = useRecoilValue(DeviceDeliCollInfoState);
  const [isGetOpticalSignalStatus, setIsGetOpticalSignalStatus] = useState<boolean>(false);
  // 「集荷状況」のポップアップ画面表示用のステータス
  const [isOpenCollectionDialog, setIsOpenCollectionDialog] = useState(false);
  // 「集荷状況」のポップアップ画面表示用のステータス
  const [isOpenDeliveryDialog, setIsOpenDeliveryDialog] = useState(false);

  const setCollection = useSetRecoilState(CollectionInfoState);
  const [isCollectionFlg, setIsCollectionFlg] = useState<string>('');
  const setDelivery = useSetRecoilState(DeliveryInfoState);

  // ポップアップオープン(集荷状況)
  const collectionOpen = (collection: CollectionInfo, collectionFlg: string) => {
    setIsOpenCollectionDialog(true);
    setCollection(collection);
    setIsCollectionFlg(collectionFlg);
  };

  // ポップアップオープン(配送状況)
  const deliveryOpen = (delivery: DeliveryInfo) => {
    setIsOpenDeliveryDialog(true);
    setDelivery(delivery);
  };

  useEffect(() => {
    getDelivery(false);
    getDeviceDeliCollInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickDeliveryButton = () => {
    getDelivery(true);
    setIsGetOpticalSignalStatus(true);
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ご利用機器情報</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            {deliveryInfo.onu && deliveryInfo.onu.length !== 0 && (
              <div>
                <Heading as='h2'>ONU情報</Heading>
                <div className='m-table'>
                  <table>
                    <colgroup>
                      <col className='w-50' />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className='m-table__th'>機種名</th>
                        <th className='m-table__th'>ご利用状況</th>
                        <th className='m-table__th'>光信号受信状況</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryInfo.onu.map((onu, index) => (
                        <tr key={`${onu.status + index}`}>
                          <td
                            className={`m-table__td ${
                              onu.status === '回収完了' ? 'onu-ta-status-a09' : ''
                            }`}
                          >
                            {onu.model ? (
                              <div>
                                {onu.link ? (
                                  <a
                                    href={onu.link}
                                    target='_blank'
                                    rel='noreferrer'
                                    data-testid='onu_model'
                                  >
                                    {onu.model}
                                  </a>
                                ) : (
                                  <div data-testid='onu_model'>{onu.model}</div>
                                )}
                              </div>
                            ) : (
                              <span data-testid='onu_model'>-</span>
                            )}
                          </td>
                          <td
                            className={`m-table__td ${
                              onu.status === '回収完了' ? 'onu-ta-status-a09' : ''
                            }`}
                            data-testid='onu_status'
                          >
                            {onu.status}
                          </td>
                          <td
                            className={`m-table__td ${
                              onu.status === '回収完了' ? 'onu-ta-status-a09' : ''
                            }`}
                          >
                            {onu.optical_signal_status ? (
                              <div data-testid='onu_optical_signal_status'>
                                {onu.optical_signal_status}
                              </div>
                            ) : (
                              <span data-testid='onu_optical_signal_status'>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className='m-button col2'>
                    {!isGetOpticalSignalStatus && (
                      <Button
                        data-testid='optical_signal_status_button'
                        variant='secondary'
                        onClick={handleClickDeliveryButton}
                      >
                        光信号受信状況の取得
                      </Button>
                    )}
                  </div>
                  <p>
                    ※ご利用端末のバージョンまたはご契約時期・プラン等によっては、一部機能がご利用いただけない場合がございます。
                  </p>
                  <p>また、取扱説明書と実機で画面表示に差分が発生する場合がございます。</p>
                </div>
              </div>
            )}

            {deliveryInfo.ta && deliveryInfo.ta.length !== 0 && (
              <div>
                <Heading as='h2'>TA情報</Heading>
                <div className='m-table'>
                  <table>
                    <colgroup>
                      <col className='w-50' />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className='m-table__th'>機種名</th>
                        <th className='m-table__th'>ご利用状況</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryInfo.ta.map((ta, index) => (
                        <tr key={`${ta.status + index}`}>
                          <td
                            className={`m-table__td ${
                              ta.status === '回収完了' ? 'onu-ta-status-a09' : ''
                            }`}
                          >
                            {ta.model ? (
                              <div>
                                {ta.link ? (
                                  <a
                                    href={ta.link}
                                    target='_blank'
                                    rel='noreferrer'
                                    data-testid='ta_model'
                                  >
                                    {ta.model}
                                  </a>
                                ) : (
                                  <div data-testid='ta_model'>{ta.model}</div>
                                )}
                              </div>
                            ) : (
                              <span data-testid='ta_model'>-</span>
                            )}
                          </td>
                          <td
                            className={`m-table__td ${
                              ta.status === '回収完了' ? 'onu-ta-status-a09' : ''
                            }`}
                            data-testid='ta_status'
                          >
                            {ta.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div>
              <Heading as='h2'>配送情報</Heading>
              <div className='m-table'>
                <table>
                  <colgroup>
                    <col className='w-50' />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className='m-table__th'>機種名</th>
                      <th className='m-table__th'>配送状況</th>
                      <th className='m-table__th'>追跡番号</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceDeliCollInfo.delivery_list.map((delivery) => {
                      return delivery.device_list.map((device, index) => {
                        return (
                          <tr key={`${device.device_name + index}`}>
                            <td className='m-table__td'>
                              <span className='m-linkTxt'>
                                <a
                                  onClick={() => {
                                    deliveryOpen(delivery);
                                  }}
                                  href='#modal-use-01'
                                >
                                  {device.device_name}
                                </a>
                              </span>
                            </td>
                            <td className='m-table__td'>{delivery.lm_state}</td>
                            <td className='m-table__td'>
                              {delivery.track_no ? delivery.track_no : <span>-</span>}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
                <p>
                  ※機種名を押すと、配送日確認・日付変更が可能です。詳しくは
                  <a
                    href='https://support.nuro.jp/faqsupport/nuro/web/knowledge10881.html'
                    target='_blank'
                    aria-label='詳しくはこちら'
                  >
                    こちら
                  </a>
                </p>
              </div>
            </div>
            <div>
              <Heading as='h2'>回収情報</Heading>
              <div className='m-table'>
                <table>
                  <colgroup>
                    <col className='w-50' />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className='m-table__th'>機種名</th>
                      <th className='m-table__th'>回収状況</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deviceDeliCollInfo.collection_list.map((collection) => {
                      return collection.device_list.map((device, index) => {
                        return (
                          <tr key={`${device.device_name + index}`}>
                            <td className='m-table__td'>
                              <span className='m-linkTxt'>
                                <a
                                  onClick={() => {
                                    collectionOpen(collection, device.finish_flg);
                                  }}
                                  href='#modal-use-01'
                                >
                                  {device.device_name}
                                </a>
                              </span>
                            </td>
                            <td className='m-table__td'>{device.device_coll_state}</td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
                <p>
                  ※機種名を押すと、返却予定日確認・日付変更が可能です。詳しくは
                  <a
                    href='https://support.nuro.jp/faqsupport/nuro/web/knowledge9286.html'
                    target='_blank'
                    aria-label='詳しくはこちら'
                  >
                    こちら
                  </a>
                  <br />
                  ※機器のご返却先および注意事項については、
                  <a href='https://1738.jp/n37' target='_blank' aria-label='こちらの案内ページ'>
                    こちらの案内ページ
                  </a>
                  を必ずご確認ください。
                </p>
              </div>
            </div>
            <div className='m-button col2'>
              <BackLinkButton href='/top'>メニューへ戻る</BackLinkButton>
            </div>
          </div>
        </div>

        <CollectionPopup
          isCollectionFlg={isCollectionFlg}
          isOpen={isOpenCollectionDialog}
          onClose={() => {
            setIsOpenCollectionDialog(false);
          }}
        />

        <DeliveryPopup
          isOpen={isOpenDeliveryDialog}
          onClose={() => {
            setIsOpenDeliveryDialog(false);
          }}
        />

        <BreadcrumbLists />
      </main>
    </>
  );
};

export default DeliveryIndex;
